/**
 * Hook for managing product tour state
 * Tracks whether the user has completed the tour and provides controls
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

// SECURITY: Storage key now includes user ID to prevent data leakage between users
const TOUR_STORAGE_KEY_PREFIX = 'trainerpulse_tour_completed_';

function getTourStorageKey(userId: string): string {
  return `${TOUR_STORAGE_KEY_PREFIX}${userId}`;
}

// Clear tour status for a specific user (exported for logout cleanup)
export function clearTourStatus(userId: string) {
  localStorage.removeItem(getTourStorageKey(userId));
}

export function useProductTour() {
  const [hasSeenTour, setHasSeenTour] = useState<boolean | null>(null);
  const [runTour, setRunTour] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // Check if user has seen the tour
  useEffect(() => {
    const checkTourStatus = async () => {
      try {
        // Get user first - required for user-specific storage key
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setHasSeenTour(false);
          setLoading(false);
          return;
        }

        setUserId(user.id);
        const storageKey = getTourStorageKey(user.id);

        // Check localStorage for immediate response (user-specific key)
        const localCompleted = localStorage.getItem(storageKey);
        if (localCompleted === 'true') {
          setHasSeenTour(true);
          setLoading(false);
          return;
        }

        // Then check database for persistence across devices
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', user.id)
          .single();

        // If onboarding is completed, assume they've seen the tour
        // (or we can add a specific tour_completed field later)
        const completed = profile?.onboarding_completed === true;
        setHasSeenTour(completed);

        // Sync to localStorage (user-specific key)
        if (completed) {
          localStorage.setItem(storageKey, 'true');
        }
      } catch (error) {
        console.error('Error checking tour status:', error);
        setHasSeenTour(false);
      } finally {
        setLoading(false);
      }
    };

    checkTourStatus();
  }, []);

  // Mark tour as completed
  const completeTour = useCallback(async () => {
    if (!userId) return;
    try {
      localStorage.setItem(getTourStorageKey(userId), 'true');
      setHasSeenTour(true);
      setRunTour(false);
    } catch (error) {
      console.error('Error completing tour:', error);
    }
  }, [userId]);

  // Start the tour
  const startTour = useCallback(() => {
    setRunTour(true);
  }, []);

  // Reset tour (for testing or settings)
  const resetTour = useCallback(() => {
    if (!userId) return;
    localStorage.removeItem(getTourStorageKey(userId));
    setHasSeenTour(false);
  }, [userId]);

  return {
    hasSeenTour,
    runTour,
    loading,
    startTour,
    completeTour,
    resetTour,
    setRunTour,
  };
}
